/**
 * New Subscription Modal
 * 
 * A unified subscription modal component that handles both
 * Stripe and Paystack payment providers based on user currency.
 * Supports promo codes (WELCOM35 for NGN/GHS merchants).
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { connect } from "react-redux";
import {
  Modal,
  Button,
  Radio,
  Select,
  Spin,
  Tag,
  Input,
  InputNumber,
  message,
  Divider,
  Alert,
  Card,
  Row,
  Col,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  GiftOutlined,
  CrownOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { usePaystackPayment } from "react-paystack";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

// Redux actions
import {
  fetchAvailablePlans,
  fetchCurrentSubscription,
  validatePromoCode,
  clearPromoCodeStatus,
  createStripeCheckout,
  initializePaystackPayment,
  verifyPaystackPayment,
  setSelectedPlan,
  determinePaymentProvider,
  cancelSubscription,
  resetSubscriptionState,
  previewPaystackUpgrade,
  addPaystackStaff,
  addPaystackBranches,
  upgradeSubscription,
} from "../../redux/actions/subscriptionActions";
import { signoutUser } from "../../redux/actions/authActions";
import { getAllBusinessBranches } from "../../redux/actions/branchActions";
import { addMerchantCurrentPlan, getPricingPlanData } from "../../redux/actions/merchantAction";

// Components
import StripeCheckoutForm from "./StripeCheckoutForm";

// Styles
import "./NewSubscriptionModal.scss";
import strings from "../../strings";

const { Title, Text } = Typography;
const { Option } = Select;

const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_KEY;
const STRIPE_CLIENT_KEY = process.env.REACT_APP_STRIPE_CLIENT_KEY;
const stripePromise = loadStripe(STRIPE_CLIENT_KEY);

// Currencies that should use Paystack
const PAYSTACK_CURRENCIES = ["NGN", "GHS", "KES", "ZAR"];

// Currencies eligible for WELCOM35 promo code (5% discount)
const WELCOM35_CURRENCIES = ["NGN", "GHS"];

// VAT rates by currency/country (percentage)
const VAT_RATES = {
  NGN: 7.5,  // Nigeria VAT
  GHS: 15,   // Ghana VAT (includes NHIL, GETFund, COVID levy)
  KES: 16,   // Kenya VAT
  ZAR: 15,   // South Africa VAT
  USD: 0,    // USA - no VAT on SaaS typically
  GBP: 20,   // UK VAT
  EUR: 19,   // EU average (Germany)
};

// Billing period options matching API spec
// Only 12-month subscriptions get 1-month free discount
const BILLING_PERIODS = [
  { value: "1-month", label: "1 Month", months: 1, discount: 0 },
  { value: "2-month", label: "2 Months", months: 2, discount: 0 },
  { value: "3-months", label: "3 Months (Quarterly)", months: 3, discount: 0 },
  { value: "6-months", label: "6 Months (Semi-Annual)", months: 6, discount: 0 },
  { value: "12-months", label: "12 Months (Annual) - 1 Month Free!", months: 12, discount: 1 },
];

const NewSubscriptionModal = ({
  // Props
  isOpen,
  onClose,
  user,
  isUndismissableForFreePlan = false,
  isTrialMode = false, // New: When true, show only trial plan for new signups
  onTrialPaymentSuccess = null, // New: Callback after successful trial payment
  
  // Redux state
  subscription,
  allBranches,
  loadingAllBranches,
  
  // Redux actions
  fetchAvailablePlans,
  fetchCurrentSubscription,
  validatePromoCode,
  clearPromoCodeStatus,
  createStripeCheckout,
  initializePaystackPayment,
  verifyPaystackPayment,
  setSelectedPlan,
  determinePaymentProvider,
  cancelSubscription,
  resetSubscriptionState,
  previewPaystackUpgrade,
  addPaystackStaff,
  addPaystackBranches,
  upgradeSubscription,
  signoutUser,
  getAllBusinessBranches,
  addMerchantCurrentPlan,
  getPricingPlanData,
}) => {
  // Local state
  const [step, setStep] = useState("select-plan"); // select-plan, payment, success
  const [selectedPlanLocal, setSelectedPlanLocal] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("1-month"); // API format billing period
  const [promoCode, setPromoCode] = useState("");
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [action, setAction] = useState("subscribe"); // subscribe, cancel
  const [numOfStaffs, setNumOfStaffs] = useState(0);
  
  // Proration state for mid-subscription upgrades
  const [prorationPreview, setProrationPreview] = useState(null);
  const [isLoadingProration, setIsLoadingProration] = useState(false);
  const [numOfBranches, setNumOfBranches] = useState(0);
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card"); // "card" or "bank_transfer"
  
  // Get current billing period config
  const getCurrentBillingPeriod = () => BILLING_PERIODS.find(bp => bp.value === billingPeriod) || BILLING_PERIODS[0];
  const duration = getCurrentBillingPeriod().months; // For backwards compatibility
  
  // Addon pricing state
  const [staffPricingData, setStaffPricingData] = useState({ price: 0, discount_percentage: 0 });
  const [branchPricingData, setBranchPricingData] = useState({ price: 0, discount_percentage: 0 });
  const [ecommercePricingData, setEcommercePricingData] = useState({ price: 0 });
  const [numOfEcommerceBranches, setNumOfEcommerceBranches] = useState(0);
  
  // User-selected addon quantities (additional staff/branches to purchase)
  const [additionalStaff, setAdditionalStaff] = useState(0);
  const [additionalBranches, setAdditionalBranches] = useState(0);
  const [additionalEcommerce, setAdditionalEcommerce] = useState(0);
  
  // Trial plan state
  const [trialPlan, setTrialPlan] = useState(null);
  const [loadingTrialPlan, setLoadingTrialPlan] = useState(false);
  
  // Prevent duplicate initialize calls
  const initializingRef = useRef(false);
  
  // Trial period duration in days
  const TRIAL_PERIOD_DAYS = 14;

  const {
    loading,
    availablePlans,
    currentSubscription,
    promoCodeStatus,
    paymentProvider,
    checkoutSession,
    error,
  } = subscription;

  const business = user?.employer || user;
  const currency = business?.currency || "USD";
  const isPaystackUser = PAYSTACK_CURRENCIES.includes(currency);
  const isEligibleForWelcom35 = WELCOM35_CURRENCIES.includes(currency);
  const isFreePlan = business?.subscription_plan === "Free";
  
  // Determine if bank transfer is available (6 months and above)
  const canUseBankTransfer = duration >= 6 && isPaystackUser && !isTrialMode;
  
  // Determine if this is a mid-subscription upgrade (user has active sub and is adding more staff/branches)
  const isMidSubscriptionUpgrade = useMemo(() => {
    if (!currentSubscription || isTrialMode || isFreePlan) return false;
    const isActive = currentSubscription.status === "active" || currentSubscription.status === "trialing";
    const hasAdditions = additionalStaff > 0 || additionalBranches > 0;
    return isActive && hasAdditions;
  }, [currentSubscription, isTrialMode, isFreePlan, additionalStaff, additionalBranches]);

  // Determine pricing multiplier based on duration
  const getDurationMultiplier = (months) => {
    return months;
  };

  // Fetch data on mount
  useEffect(() => {
    if (isOpen) {
      fetchAvailablePlans();
      // Don't call fetchCurrentSubscription here as it updates subscriptionStatus
      // which can cause the parent SubscriptionNotification to unmount
      // fetchCurrentSubscription();
      getAllBusinessBranches();
      determinePaymentProvider(currency);
      
      // Fetch addon pricing data (staff, branch, ecommerce)
      const fetchAddonPricing = async () => {
        try {
          const [staffRes, branchRes, ecommerceRes] = await Promise.all([
            getPricingPlanData({ plan_name: "Staff" }),
            getPricingPlanData({ plan_name: "Branch" }),
            getPricingPlanData({ plan_name: "Ecommerce" }),
          ]);
          
          if (staffRes?.data) setStaffPricingData(staffRes.data);
          if (branchRes?.data) setBranchPricingData(branchRes.data);
          if (ecommerceRes?.data) setEcommercePricingData(ecommerceRes.data);
        } catch (err) {
          console.error("Failed to fetch addon pricing:", err);
        }
      };
      
      fetchAddonPricing();
    }
  }, [isOpen, currency]);

  // Reset payment method when billing period changes and bank transfer is no longer available
  useEffect(() => {
    if (!canUseBankTransfer && paymentMethod === "bank_transfer") {
      setPaymentMethod("card");
    }
  }, [billingPeriod, canUseBankTransfer, paymentMethod]);

  // Fetch Trial plan when in trial mode
  useEffect(() => {
    if (isOpen && isTrialMode) {
      const fetchTrialPlan = async () => {
        setLoadingTrialPlan(true);
        try {
          const trialPlanRes = await getPricingPlanData({ plan_name: "Trial" });
          if (trialPlanRes?.data) {
            // Format plan data to match standardPlans structure
            const formattedTrialPlan = {
              id: trialPlanRes.data.id || "trial",
              plan_name: "Trial",
              name: "Trial",
              price: trialPlanRes.data.price || 0,
              currency: trialPlanRes.data.currency || currency,
              description: `${TRIAL_PERIOD_DAYS}-day paid trial with full access`,
              features: [
                `${TRIAL_PERIOD_DAYS} days of full access`,
                "All features unlocked",
                "No commitment",
                "Upgrade anytime",
              ],
              duration_months: 1, // Not really used for trial
              ...trialPlanRes.data,
            };
            setTrialPlan(formattedTrialPlan);
            setSelectedPlanLocal(formattedTrialPlan);
          }
        } catch (err) {
          console.error("Failed to fetch Trial plan:", err);
          message.error("Failed to load trial plan. Please try again.");
        } finally {
          setLoadingTrialPlan(false);
        }
      };
      
      fetchTrialPlan();
    }
  }, [isOpen, isTrialMode, currency]);

  // Calculate staff and branches from allBranches
  useEffect(() => {
    if (allBranches?.length) {
      const filteredBranches = allBranches.filter(
        (branch) => branch.name !== "Ecommerce"
      );
      const ecommerceBranches = allBranches.filter(
        (branch) => branch.name === "Ecommerce"
      );
      
      setNumOfBranches(filteredBranches.length);
      setNumOfEcommerceBranches(ecommerceBranches.length);

      const staffs = filteredBranches.flatMap((branch) => branch.staff || []);
      setNumOfStaffs(staffs.length);
    }
  }, [allBranches]);

  // Fetch proration preview for mid-subscription upgrades (Paystack only)
  useEffect(() => {
    const fetchProrationPreview = async () => {
      if (!isMidSubscriptionUpgrade || !isPaystackUser) {
        setProrationPreview(null);
        return;
      }
      
      // Only fetch if user is adding staff or branches
      if (additionalStaff <= 0 && additionalBranches <= 0) {
        setProrationPreview(null);
        return;
      }
      
      setIsLoadingProration(true);
      try {
        const previewData = {
          preview: true,
          add_staff: additionalStaff,
          add_branches: additionalBranches,
        };
        
        const result = await previewPaystackUpgrade(previewData);
        if (result?.preview) {
          setProrationPreview(result.preview);
        }
      } catch (err) {
        console.error("Failed to fetch proration preview:", err);
        setProrationPreview(null);
      } finally {
        setIsLoadingProration(false);
      }
    };
    
    // Debounce the proration preview fetch
    const timeoutId = setTimeout(fetchProrationPreview, 500);
    return () => clearTimeout(timeoutId);
  }, [isMidSubscriptionUpgrade, isPaystackUser, additionalStaff, additionalBranches, previewPaystackUpgrade]);

  // Check for pending Paystack payment on mount (after redirect from Paystack)
  useEffect(() => {
    const checkPendingPayment = async () => {
      // Check URL for Paystack reference
      const urlParams = new URLSearchParams(window.location.search);
      const urlReference = urlParams.get('reference') || urlParams.get('trxref');
      
      // Also check localStorage for pending reference
      const storedReference = localStorage.getItem('pending_paystack_reference');
      const reference = urlReference || storedReference;

      if (reference) {
        console.log("Found pending Paystack payment reference:", reference);
        setIsProcessing(true);
        
        try {
          // Verify the payment
          const result = await verifyPaystackPayment(reference);
          console.log("Pending payment verification result:", result);
          
          const isVerified = result?.status === "success" || result?.success === true;
          
          if (isVerified) {
            message.success("Payment verified successfully!");
            setStep("success");
            
            // Get stored info before clearing
            const wasTrial = localStorage.getItem('pending_paystack_is_trial') === 'true';
            const planName = localStorage.getItem('pending_paystack_plan_name') || 'Subscription';
            
            // Clear pending payment data
            localStorage.removeItem('pending_paystack_reference');
            localStorage.removeItem('pending_paystack_plan_id');
            localStorage.removeItem('pending_paystack_is_trial');
            localStorage.removeItem('pending_paystack_plan_name');
            
            // Clear URL params
            if (urlReference) {
              window.history.replaceState({}, document.title, window.location.pathname);
            }
            
            console.log("Payment verified - subscription created automatically by backend");

            // Show success and sign out to refresh session with new subscription
            setTimeout(() => {
              Modal.success({
                title: wasTrial ? "Trial Activated!" : "Subscription Successful!",
                content: `Your ${planName} plan is now active. You will be logged out. Please log back in to continue.`,
                onOk: () => {
                  signoutUser();
                },
              });
            }, 1500);
          } else {
            console.error("Payment verification failed:", result);
            message.error("Payment verification failed. Please contact support.");
            // Clear pending data on failure
            localStorage.removeItem('pending_paystack_reference');
            localStorage.removeItem('pending_paystack_plan_id');
            localStorage.removeItem('pending_paystack_is_trial');
            localStorage.removeItem('pending_paystack_plan_name');
          }
        } catch (err) {
          console.error("Error verifying pending payment:", err);
          message.error("Failed to verify payment: " + (err.message || "Unknown error"));
        } finally {
          setIsProcessing(false);
        }
      }
    };

    checkPendingPayment();
  }, [verifyPaystackPayment, signoutUser]);

  // Handle promo code validation
  const handleValidatePromoCode = async () => {
    if (!promoCode.trim()) {
      message.warning("Please enter a promo code");
      return;
    }

    setIsValidatingPromo(true);
    const result = await validatePromoCode(promoCode);
    setIsValidatingPromo(false);

    if (result?.valid) {
      message.success(result.message || "Promo code applied successfully!");
    } else {
      message.error(result?.error || "Invalid promo code");
    }
  };

  // Clear promo code
  const handleClearPromoCode = () => {
    setPromoCode("");
    clearPromoCodeStatus();
  };

  // Select a plan
  const handleSelectPlan = (plan) => {
    setSelectedPlanLocal(plan);
    setSelectedPlan(plan);
  };

  // Calculate extra staff count (staff beyond plan limit + user-selected additional)
  const getExtraStaffCount = () => {
    if (!selectedPlanLocal) return additionalStaff;
    const maxStaff = selectedPlanLocal.max_staff || 0;
    const existingExtra = Math.max(0, numOfStaffs - maxStaff);
    return existingExtra + additionalStaff;
  };

  // Calculate extra branch count (branches beyond plan limit + user-selected additional)
  const getExtraBranchCount = () => {
    if (!selectedPlanLocal) return additionalBranches;
    const maxBranches = selectedPlanLocal.max_branches || 1; // At least 1 branch is included
    const existingExtra = Math.max(0, numOfBranches - maxBranches);
    return existingExtra + additionalBranches;
  };
  
  // Get total ecommerce branches (existing + additional)
  const getTotalEcommerceBranches = () => {
    return numOfEcommerceBranches + additionalEcommerce;
  };

  // Calculate staff price with discount
  const calculateStaffPrice = () => {
    const extraStaff = getExtraStaffCount();
    if (extraStaff <= 0) return 0;
    
    const staffPrice = parseFloat(staffPricingData.price) || 0;
    const discountPercent = parseFloat(staffPricingData.discount_percentage) || 0;
    const discountedPrice = staffPrice * (1 - discountPercent);
    
    return discountedPrice * extraStaff * duration;
  };

  // Calculate branch price with discount
  const calculateBranchPrice = () => {
    const extraBranches = getExtraBranchCount();
    if (extraBranches <= 0) return 0;
    
    const branchPrice = parseFloat(branchPricingData.price) || 0;
    const discountPercent = parseFloat(branchPricingData.discount_percentage) || 0;
    const discountedPrice = branchPrice * (1 - discountPercent);
    
    return discountedPrice * extraBranches * duration;
  };

  // Calculate ecommerce branch price
  const calculateEcommercePrice = () => {
    const totalEcommerce = getTotalEcommerceBranches();
    if (totalEcommerce <= 0) return 0;
    
    const ecommercePrice = parseFloat(ecommercePricingData.price) || 0;
    return ecommercePrice * totalEcommerce * duration;
  };

  // Get VAT rate for current currency
  const getVatRate = () => VAT_RATES[currency] || 0;

  // Calculate subtotal (before discount and VAT)
  const calculateSubtotal = () => {
    if (!selectedPlanLocal) return 0;

    const basePrice = parseFloat(selectedPlanLocal.price) || 0;
    const months = getDurationMultiplier(duration);
    
    // Base plan price
    let subtotal = basePrice * months;
    
    // Add addon costs (staff and branches only - ecommerce is an app addon)
    subtotal += calculateStaffPrice();
    subtotal += calculateBranchPrice();

    return subtotal;
  };

  // Calculate annual discount (1 month free for 12-month subscriptions)
  const calculateAnnualDiscount = () => {
    const billingConfig = getCurrentBillingPeriod();
    if (billingConfig.discount <= 0 || !selectedPlanLocal) return 0;
    
    // Discount = base plan price * number of months discounted
    const basePrice = parseFloat(selectedPlanLocal.price) || 0;
    return basePrice * billingConfig.discount;
  };

  // Calculate VAT amount
  const calculateVat = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateAnnualDiscount();
    const promoDiscount = promoCodeStatus?.valid && promoCodeStatus?.discountPercent
      ? (subtotal - discount) * (promoCodeStatus.discountPercent / 100)
      : 0;
    
    const taxableAmount = subtotal - discount - promoDiscount;
    const vatRate = getVatRate();
    
    return taxableAmount * (vatRate / 100);
  };

  // Calculate total price including addons, discounts, and VAT
  const calculateTotal = () => {
    if (!selectedPlanLocal) return 0;

    const subtotal = calculateSubtotal();
    const annualDiscount = calculateAnnualDiscount();
    
    // Subtotal after annual discount
    let total = subtotal - annualDiscount;

    // Apply promo code discount
    if (promoCodeStatus?.valid && promoCodeStatus?.discountPercent) {
      total = total * (1 - promoCodeStatus.discountPercent / 100);
    }

    // Add VAT
    const vatRate = getVatRate();
    if (vatRate > 0) {
      total = total * (1 + vatRate / 100);
    }

    return total.toFixed(2);
  };

  // Get full pricing breakdown for display
  const getPricingBreakdown = () => {
    const subtotal = calculateSubtotal();
    const annualDiscount = calculateAnnualDiscount();
    const promoDiscount = promoCodeStatus?.valid && promoCodeStatus?.discountPercent
      ? (subtotal - annualDiscount) * (promoCodeStatus.discountPercent / 100)
      : 0;
    const subtotalAfterDiscount = subtotal - annualDiscount - promoDiscount;
    const vatRate = getVatRate();
    const vatAmount = subtotalAfterDiscount * (vatRate / 100);
    const total = subtotalAfterDiscount + vatAmount;
    
    return {
      basePlanPrice: parseFloat(selectedPlanLocal?.price || 0),
      basePlanTotal: parseFloat(selectedPlanLocal?.price || 0) * duration,
      staffTotal: calculateStaffPrice(),
      branchTotal: calculateBranchPrice(),
      subtotal,
      annualDiscount,
      annualMonthsFree: getCurrentBillingPeriod().discount,
      promoDiscount,
      promoPercent: promoCodeStatus?.discountPercent || 0,
      subtotalAfterDiscount,
      vatRate,
      vatAmount,
      total,
      billingPeriod: getCurrentBillingPeriod(),
    };
  };

  // Get addon summary for display and metadata
  const getAddonSummary = useCallback(() => {
    return {
      extra_staff: getExtraStaffCount(),
      extra_staff_price: calculateStaffPrice(),
      extra_branches: getExtraBranchCount(),
      extra_branch_price: calculateBranchPrice(),
      staff_unit_price: parseFloat(staffPricingData.price) || 0,
      branch_unit_price: parseFloat(branchPricingData.price) || 0,
      // User-selected additions
      additional_staff: additionalStaff,
      additional_branches: additionalBranches,
    };
  }, [selectedPlanLocal, numOfStaffs, additionalStaff, staffPricingData, duration, numOfBranches, additionalBranches, branchPricingData]);

  // Format currency
  const formatCurrency = (amount, currencyCode = currency) => {
    const symbols = {
      USD: "$",
      GBP: "£",
      EUR: "€",
      NGN: "₦",
      GHS: "GH₵",
      KES: "KSh",
      ZAR: "R",
    };
    return `${symbols[currencyCode] || currencyCode} ${parseFloat(amount).toFixed(2)}`;
  };

  // Handle Stripe checkout
  const handleStripeCheckout = async () => {
    if (!selectedPlanLocal) {
      message.error("Please select a plan");
      return;
    }

    setIsProcessing(true);

    try {
      const addonSummary = getAddonSummary();
      const billingConfig = getCurrentBillingPeriod();
      
      // Check if this is a mid-subscription upgrade for Stripe users
      if (isMidSubscriptionUpgrade && currentSubscription?.id && !isPaystackUser) {
        console.log("Processing mid-subscription upgrade for Stripe user...");
        
        const upgradeData = {
          add_staff: additionalStaff,
          add_branches: additionalBranches,
        };
        
        const result = await upgradeSubscription(currentSubscription.id, upgradeData);
        if (result) {
          message.success("Subscription upgraded successfully!");
          setStep("success");
          fetchCurrentSubscription();
        } else {
          throw new Error("Failed to upgrade subscription");
        }
        
        setIsProcessing(false);
        return;
      }
      
      // Regular new subscription flow
      const checkoutData = {
        pricing_plan_id: selectedPlanLocal.id,
        promo_code: promoCodeStatus?.valid ? promoCode : undefined,
        billing_period: isTrialMode ? "1-month" : billingPeriod,
        billing_months: isTrialMode ? 1 : billingConfig.months,
        is_trial: isTrialMode,
        trial_days: isTrialMode ? TRIAL_PERIOD_DAYS : undefined,
        addons: isTrialMode ? {} : {
          extra_staff: addonSummary.extra_staff,
          extra_branches: addonSummary.extra_branches,
        },
        success_url: isTrialMode 
          ? `${window.location.origin}/d/sales?trial=success`
          : `${window.location.origin}/d/account?subscription=success`,
        cancel_url: isTrialMode
          ? `${window.location.origin}/d/sales?trial=cancelled`
          : `${window.location.origin}/d/account?subscription=cancelled`,
      };

      const result = await createStripeCheckout(checkoutData);

      if (result?.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkout_url;
      } else if (result?.client_secret) {
        // Use embedded checkout
        setStripeClientSecret(result.client_secret);
        setShowStripeCheckout(true);
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (err) {
      message.error(err.message || "Failed to initiate checkout");
    } finally {
      setIsProcessing(false);
    }
  };

  // Get addon summary for Paystack metadata
  const addonSummaryForPaystack = getAddonSummary();

  // Calculate trial amount (no addons for trial)
  const getTrialAmount = () => {
    if (!trialPlan) return 0;
    return Math.round(parseFloat(trialPlan.price || 0) * 100);
  };

  // Paystack close callback
  const onPaystackClose = useCallback(() => {
    console.log("PaystackPop closed by user");
    message.info("Payment cancelled");
    setIsProcessing(false);
  }, []);

  // Paystack success callback
  const onPaystackSuccess = useCallback(async (response) => {
    console.log("Paystack payment successful, response:", response);
    console.log("Response type:", typeof response);
    console.log("Response keys:", Object.keys(response || {}));
    setIsProcessing(true);

    try {
      // PaystackPop returns {reference: "...", status: "success"}
      // Handle both react-paystack hook format and PaystackPop format
      console.log("Extracting reference from response...");
      console.log("response?.reference:", response?.reference);
      const transactionRef = response?.reference || response;
      
      console.log("Final transactionRef:", transactionRef);
      console.log("transactionRef type:", typeof transactionRef);
      
      if (!transactionRef) {
        throw new Error("No transaction reference received from payment provider");
      }

      console.log("✓ Sending to verification API with reference:", transactionRef);
      const result = await verifyPaystackPayment(transactionRef);

      console.log("Verification result:", result);
      console.log("Result status:", result?.status, "Result success:", result?.success, "Type:", typeof result?.status);
      const isVerified = result?.status === "success" || result?.success === true;
      console.log("Is verified (status === 'success' OR success === true)?", isVerified);
      
      if (isVerified) {
        console.log("✓ Payment verification succeeded, showing success message");
        message.success(isTrialMode 
          ? "Trial activated successfully!" 
          : "Subscription activated successfully!"
        );
        console.log("Setting step to 'success'");
        setStep("success");
        console.log("✓ Step set to success");

        // Calculate expiry - use trial days for trial mode, otherwise normal duration
        const currentDate = new Date();
        const expiresOn = new Date(currentDate);
        const daysToAdd = isTrialMode ? TRIAL_PERIOD_DAYS : 28 * duration;
        expiresOn.setDate(expiresOn.getDate() + daysToAdd);

        const addonInfo = getAddonSummary();
        console.log("Adding merchant plan with:", {
          plan: isTrialMode ? "Trial" : selectedPlanLocal?.name,
          currency_code: currency,
          no_of_days: daysToAdd,
          transaction_reference: transactionRef,
        });

        await addMerchantCurrentPlan(user?.email, {
          plan: isTrialMode ? "Trial" : selectedPlanLocal?.name,
          currency_code: currency,
          no_of_days: daysToAdd,
          expires_on: expiresOn.toLocaleDateString(),
          transaction_reference: transactionRef,
          is_trial: isTrialMode,
          // Addon details (not applicable for trial)
          extra_staff: isTrialMode ? 0 : addonInfo.extra_staff,
          extra_branches: isTrialMode ? 0 : addonInfo.extra_branches,
          ecommerce_branches: isTrialMode ? 0 : addonInfo.ecommerce_branches,
          total_staff_price: isTrialMode ? 0 : addonInfo.extra_staff_price,
          total_branch_price: isTrialMode ? 0 : addonInfo.extra_branch_price,
          total_ecommerce_price: isTrialMode ? 0 : addonInfo.ecommerce_price,
          num_of_staffs: numOfStaffs,
          num_of_branches: numOfBranches,
        });

        console.log("Merchant plan added successfully");

        // For trial mode, call the callback if provided
        if (isTrialMode && onTrialPaymentSuccess) {
          console.log("Calling trial payment success callback");
          setTimeout(() => {
            onTrialPaymentSuccess();
          }, 1500);
        } else {
          // Sign out to refresh session for regular subscriptions
          console.log("Showing success modal and signing out");
          setTimeout(() => {
            Modal.success({
              title: isTrialMode ? "Trial Activated!" : "Subscription Successful!",
              content: "You will be logged out. Please log back in to continue.",
              onOk: () => {
                console.log("User clicked OK, signing out");
                signoutUser();
              },
            });
          }, 1500);
        }
      } else {
        console.error("✗ Payment verification failed - verification check returned false. Full result:", result);
        throw new Error(`Payment verification returned status: ${result?.status || result?.success || 'unknown'}`);
      }
    } catch (err) {
      console.error("✗ Error in payment processing:", {
        error: err,
        message: err.message,
        stack: err.stack
      });
      message.error(err.message || "Payment verification failed");
    } finally {
      console.log("Payment processing complete (finally block)");
      setIsProcessing(false);
    }
  }, [isTrialMode, duration, currency, selectedPlanLocal, user, onTrialPaymentSuccess, verifyPaystackPayment, addMerchantCurrentPlan, signoutUser, TRIAL_PERIOD_DAYS, numOfStaffs, numOfBranches, getAddonSummary]);

  // Paystack configuration with addons (or trial mode)
  const paystackConfig = {
    email: user?.email,
    amount: isTrialMode ? getTrialAmount() : Math.round(parseFloat(calculateTotal()) * 100), // Paystack expects amount in kobo/pesewas
    publicKey: PAYSTACK_PUBLIC_KEY,
    currency: currency,
    metadata: {
      merchant_id: business?.id,
      plan_id: isTrialMode ? trialPlan?.id : selectedPlanLocal?.id,
      plan_name: isTrialMode ? "Trial" : selectedPlanLocal?.name,
      duration: isTrialMode ? TRIAL_PERIOD_DAYS : duration,
      is_trial: isTrialMode,
      trial_days: isTrialMode ? TRIAL_PERIOD_DAYS : null,
      promo_code: promoCodeStatus?.valid ? promoCode : null,
      // Addon information (not used for trial)
      addons: isTrialMode ? {} : {
        extra_staff: addonSummaryForPaystack.extra_staff,
        extra_staff_price: addonSummaryForPaystack.extra_staff_price,
        extra_branches: addonSummaryForPaystack.extra_branches,
        extra_branch_price: addonSummaryForPaystack.extra_branch_price,
        ecommerce_branches: addonSummaryForPaystack.ecommerce_branches,
        ecommerce_price: addonSummaryForPaystack.ecommerce_price,
      },
      // Pricing breakdown
      base_price: isTrialMode ? parseFloat(trialPlan?.price || 0) : parseFloat(selectedPlanLocal?.price || 0) * duration,
      total_staff_price: isTrialMode ? 0 : addonSummaryForPaystack.extra_staff_price,
      total_branch_price: isTrialMode ? 0 : addonSummaryForPaystack.extra_branch_price,
      total_ecommerce_price: isTrialMode ? 0 : addonSummaryForPaystack.ecommerce_price,
      num_of_staffs: numOfStaffs,
      num_of_branches: numOfBranches,
      num_of_ecommerce_branches: numOfEcommerceBranches,
    },
  };

  // Initialize Paystack hook with config
  const initializePayment = usePaystackPayment(paystackConfig);

  // Handle Paystack checkout
  const handlePaystackCheckout = useCallback(async () => {
    console.log("handlePaystackCheckout called");
    
    // Prevent duplicate calls using ref (more reliable than state)
    if (initializingRef.current) {
      console.log("Already initializing payment, ignoring duplicate click");
      return;
    }
    
    if (!selectedPlanLocal && !isTrialMode) {
      message.error("Please select a plan");
      return;
    }

    initializingRef.current = true;
    setIsProcessing(true);
    try {
      // Check if this is a mid-subscription upgrade
      if (isMidSubscriptionUpgrade && currentSubscription?.id) {
        console.log("Processing mid-subscription upgrade with proration...");
        
        // Add staff if requested
        if (additionalStaff > 0) {
          const staffResult = await addPaystackStaff(currentSubscription.id, additionalStaff);
          if (!staffResult) {
            throw new Error("Failed to add staff to subscription");
          }
          console.log("Added staff successfully:", staffResult);
        }
        
        // Add branches if requested
        if (additionalBranches > 0) {
          const branchResult = await addPaystackBranches(currentSubscription.id, additionalBranches);
          if (!branchResult) {
            throw new Error("Failed to add branches to subscription");
          }
          console.log("Added branches successfully:", branchResult);
        }
        
        // Show success message
        const chargedAmount = prorationPreview?.prorated_amount || 0;
        message.success(`Upgrade successful! Charged ${formatCurrency(chargedAmount)} for prorated period.`);
        setStep("success");
        
        // Refresh data
        fetchCurrentSubscription();
        
        initializingRef.current = false;
        setIsProcessing(false);
        return;
      }
      
      // Regular new subscription flow
      console.log("Initializing Paystack payment for new subscription...");
      
      // Build callback URL - use current origin and path
      const callbackUrl = `${window.location.origin}${window.location.pathname}`;
      
      // Build addons object per API spec - include FULL extra counts (existing overage + user additions)
      const addonSummary = getAddonSummary();
      const addons = isTrialMode ? {} : {
        extra_staff: addonSummary.extra_staff,
        extra_branches: addonSummary.extra_branches,
      };
      
      const billingConfig = getCurrentBillingPeriod();
      const initData = {
        pricing_plan_id: isTrialMode ? trialPlan?.id : selectedPlanLocal?.id,
        callback_url: callbackUrl,
        // Include billing_period per API spec (defaults to '1-month')
        billing_period: isTrialMode ? '1-month' : billingPeriod,
        // Include numeric billing_months so backend can calculate amount correctly
        billing_months: isTrialMode ? 1 : billingConfig.months,
        // Include addons per API spec
        addons: addons,
        // Include payment method for bank transfer on 6+ month subscriptions
        ...(paymentMethod === "bank_transfer" && { payment_method: "bank_transfer" }),
      };
      
      console.log("Initialize data being sent:", initData);
      const initResult = await initializePaystackPayment(initData);
      console.log("Initialize result:", initResult);

      if (!initResult?.authorization_url) {
        throw new Error("Failed to get authorization URL from backend");
      }

      console.log("✓ Got Paystack authorization URL:", initResult.authorization_url);
      console.log("✓ Reference for verification:", initResult.reference);

      // Store reference and metadata in localStorage for verification when user returns
      localStorage.setItem('pending_paystack_reference', initResult.reference);
      localStorage.setItem('pending_paystack_plan_id', isTrialMode ? trialPlan?.id : selectedPlanLocal?.id);
      localStorage.setItem('pending_paystack_is_trial', isTrialMode ? 'true' : 'false');
      localStorage.setItem('pending_paystack_plan_name', isTrialMode ? 'Trial' : selectedPlanLocal?.name);

      // Redirect to Paystack authorization URL
      window.location.href = initResult.authorization_url;

    } catch (err) {
      console.error("Paystack initialization error:", err);
      message.error("Failed to initialize payment: " + (err.message || "Unknown error"));
      initializingRef.current = false;
      setIsProcessing(false);
    }
  }, [selectedPlanLocal, isTrialMode, billingPeriod, currency, trialPlan, initializePaystackPayment, TRIAL_PERIOD_DAYS, additionalStaff, additionalBranches, isMidSubscriptionUpgrade, currentSubscription, addPaystackStaff, addPaystackBranches, prorationPreview, formatCurrency, fetchCurrentSubscription]);

  // Handle payment based on provider
  const handlePayment = () => {
    if (isPaystackUser) {
      handlePaystackCheckout();
    } else {
      handleStripeCheckout();
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    if (!currentSubscription?.id) {
      message.error("No active subscription found");
      return;
    }

    Modal.confirm({
      title: "Cancel Subscription",
      content: "Are you sure you want to cancel your subscription? Your access will continue until the end of the current billing period.",
      okText: "Yes, Cancel",
      okType: "danger",
      cancelText: "Keep Subscription",
      onOk: async () => {
        setIsProcessing(true);
        try {
          const result = await cancelSubscription(currentSubscription.id, false, "user_requested");
          if (result) {
            message.success("Subscription will be cancelled at the end of the billing period");
            onClose();
          }
        } catch (err) {
          message.error(err.message || "Failed to cancel subscription");
        } finally {
          setIsProcessing(false);
        }
      },
    });
  };

  // Get plan icon
  const getPlanIcon = (planName) => {
    const name = planName?.toLowerCase() || "";
    if (name.includes("pro")) return <CrownOutlined />;
    if (name.includes("plus")) return <RocketOutlined />;
    return <ThunderboltOutlined />;
  };

  // Get plan color for UI
  const getPlanColor = (planName) => {
    const name = planName?.toLowerCase() || "";
    if (name.includes("pro")) return "#722ed1";
    if (name.includes("plus")) return "#1890ff";
    return "#52c41a";
  };

  // Render plan card
  const renderPlanCard = (plan) => {
    const isSelected = selectedPlanLocal?.id === plan.id;
    const planColor = getPlanColor(plan.name);

    return (
      <Card
        key={plan.id}
        className={`plan-card ${isSelected ? "selected" : ""}`}
        style={{
          borderColor: isSelected ? planColor : "#d9d9d9",
          cursor: "pointer",
        }}
        onClick={() => handleSelectPlan(plan)}
      >
        <div className="plan-header" style={{ color: planColor }}>
          {getPlanIcon(plan.name)}
          <Title level={4} style={{ margin: "8px 0", color: planColor }}>
            {plan.name}
          </Title>
        </div>

        <div className="plan-price">
          <Text strong style={{ fontSize: "24px" }}>
            {formatCurrency(plan.price, plan.currency_code)}
          </Text>
          <Text type="secondary"> / month</Text>
        </div>

        <Divider style={{ margin: "12px 0" }} />

        <ul className="plan-features">
          <li>Up to {plan.max_staff || "Unlimited"} staff</li>
          <li>Up to {plan.max_branches || "Unlimited"} branch</li>
          <li>Up to {plan.max_products || "Unlimited"} products</li>
          {plan.sms_allowed && <li>SMS notifications included</li>}
         
        </ul>

        {isSelected && (
          <Tag color={planColor} className="selected-tag">
            <CheckCircleOutlined /> Selected
          </Tag>
        )}
      </Card>
    );
  };

  // Modal footer
  const getModalFooter = () => {
    if (step === "success") {
      return [
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ];
    }

    if (showStripeCheckout) {
      return [
        <Button
          key="back"
          onClick={() => {
            setShowStripeCheckout(false);
            setStripeClientSecret(null);
          }}
        >
          Back
        </Button>,
      ];
    }

    const buttons = [];

    // Trial mode footer
    if (isTrialMode) {
      buttons.push(
        <Button
          key="start-trial"
          type="primary"
          size="large"
          loading={isProcessing || loadingTrialPlan}
          disabled={!trialPlan || loadingTrialPlan}
          onClick={handlePayment}
          style={{ minWidth: 200 }}
        >
          {isPaystackUser 
            ? `Pay ${currency} ${parseFloat(trialPlan?.price || 0).toLocaleString()} - Start Trial`
            : `Pay ${currency} ${parseFloat(trialPlan?.price || 0).toLocaleString()} - Start Trial`
          }
        </Button>
      );
      
      return buttons;
    }

    if (action === "subscribe") {
      buttons.push(
        <Button
          key="subscribe"
          type="primary"
          loading={isProcessing || loading}
          disabled={!selectedPlanLocal || loadingAllBranches}
          onClick={handlePayment}
        >
          {isPaystackUser ? "Pay with Paystack" : "Pay with Stripe"}
        </Button>
      );
    }

    if (action === "cancel" && currentSubscription?.id) {
      buttons.push(
        <Button
          key="cancel-sub"
          type="primary"
          danger
          loading={isProcessing}
          onClick={handleCancelSubscription}
        >
          Cancel Subscription
        </Button>
      );
    }

    if (!isUndismissableForFreePlan) {
      buttons.unshift(
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      );
    }

    return buttons;
  };

  // Render content based on step
  const renderContent = () => {
    if (loading && !availablePlans.standardPlans.length) {
      return (
        <div className="loading-container">
          <Spin size="large" />
          <Text>Loading plans...</Text>
        </div>
      );
    }

    if (showStripeCheckout && stripeClientSecret) {
      return (
        <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
          <StripeCheckoutForm
            clientSecret={stripeClientSecret}
            onSuccess={() => {
              setStep("success");
              setShowStripeCheckout(false);
            }}
            onError={(err) => {
              message.error(err.message || "Payment failed");
              setShowStripeCheckout(false);
            }}
            user={user}
            planName={selectedPlanLocal?.name}
            addMerchantCurrentPlan={addMerchantCurrentPlan}
            signoutUser={signoutUser}
            duration={duration}
          />
        </Elements>
      );
    }

    if (step === "success") {
      return (
        <div className="success-container">
          <CheckCircleOutlined style={{ fontSize: 64, color: "#52c41a" }} />
          <Title level={3}>Subscription Activated!</Title>
          <Text>Your subscription has been successfully activated.</Text>
        </div>
      );
    }

    return (
      <div className="subscription-content">
        {/* Error Alert */}
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Free Plan Banner */}
        {isFreePlan && (
          <Alert
            message="Free Plan"
            description="You are currently on the Free plan. Subscribe to unlock all features!"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Action Selection for Non-Free Users */}
        {!isFreePlan && currentSubscription && (
          <div className="action-selection" style={{ marginBottom: 16 }}>
            <Radio.Group
              value={action}
              onChange={(e) => setAction(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="subscribe">Subscribe/Upgrade</Radio.Button>
              <Radio.Button value="cancel">Cancel Subscription</Radio.Button>
            </Radio.Group>
          </div>
        )}

        {action === "cancel" && currentSubscription ? (
          <div className="cancel-subscription-info">
            <Alert
              message="Current Subscription"
              description={
                <div>
                  <p>Plan: <strong>{currentSubscription.pricing_plan?.name}</strong></p>
                  <p>Status: <Tag color={currentSubscription.status === "active" ? "green" : "orange"}>{currentSubscription.status}</Tag></p>
                  <p>Expires: <strong>{new Date(currentSubscription.expires_on).toLocaleDateString()}</strong></p>
                </div>
              }
              type="warning"
              showIcon
            />
          </div>
        ) : (
          <>
            {/* Trial Mode Content */}
            {isTrialMode ? (
              <div className="trial-section">
                <Alert
                  message={`Start Your ${TRIAL_PERIOD_DAYS}-Day Paid Trial`}
                  description={
                    <div>
                      <p>Get full access to all Loystar features for {TRIAL_PERIOD_DAYS} days.</p>
                      <p>No commitment - upgrade to a full subscription anytime.</p>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
                
                {loadingTrialPlan ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" />
                    <p>Loading trial plan...</p>
                  </div>
                ) : trialPlan ? (
                  <Card 
                    className="trial-plan-card"
                    style={{ 
                      maxWidth: 400, 
                      margin: '0 auto',
                      border: '2px solid #1890ff',
                      borderRadius: 8
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <Title level={3}>Trial Plan</Title>
                      <Title level={2} style={{ color: '#1890ff', margin: '16px 0' }}>
                        {currency} {parseFloat(trialPlan.price || 0).toLocaleString()}
                      </Title>
                      <Text type="secondary">for {TRIAL_PERIOD_DAYS} days</Text>
                      
                      <Divider />
                      
                      <div style={{ textAlign: 'left' }}>
                        <Title level={5}>What's included:</Title>
                        <ul style={{ paddingLeft: 20 }}>
                          <li>Full access to all features</li>
                          <li>Unlimited sales recording</li>
                          <li>Customer management</li>
                          <li>Inventory tracking</li>
                          <li>Reports & analytics</li>
                          <li>No commitment required</li>
                        </ul>
                      </div>
                    </div>
                  </Card>
                ) : (
                  <Alert
                    message="Trial plan not available"
                    description="Unable to load trial plan for your currency. Please contact support."
                    type="error"
                    showIcon
                  />
                )}
              </div>
            ) : (
              <>
                {/* Plan Selection */}
                <div className="plans-section">
                  <Title level={5}>Select a Plan</Title>
                  <Row gutter={[16, 16]}>
                    {availablePlans.standardPlans.map((plan) => (
                      <Col xs={24} sm={12} md={8} key={plan.id}>
                        {renderPlanCard(plan)}
                      </Col>
                    ))}
                  </Row>
                </div>

                {/* Billing Period Selection */}
                {selectedPlanLocal && (
                  <div className="duration-section" style={{ marginTop: 24 }}>
                    <Title level={5}>Billing Period</Title>
                    <Select
                      value={billingPeriod}
                      onChange={setBillingPeriod}
                      style={{ width: 300 }}
                      size="large"
                    >
                      {BILLING_PERIODS.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                    {getCurrentBillingPeriod().discount > 0 && (
                      <Tag color="success" style={{ marginLeft: 12, fontSize: 14 }}>
                        <GiftOutlined /> 1 Month Free!
                      </Tag>
                    )}
                    {isPaystackUser && getVatRate() > 0 && (
                      <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        VAT ({getVatRate()}%) will be applied at checkout
                      </Text>
                    )}
                  </div>
                )}

                {/* Payment Method Selection (for Paystack bank transfer) */}
                {selectedPlanLocal && canUseBankTransfer && (
                  <div className="payment-method-section" style={{ marginTop: 24 }}>
                    <Title level={5}>Payment Method</Title>
                    <Radio.Group 
                      value={paymentMethod} 
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                    >
                      <Radio value="card" style={{ padding: 12, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                        <div>
                          <Text strong>Credit/Debit Card</Text>
                          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                            Instant payment with recurring billing
                          </Text>
                        </div>
                      </Radio>
                      <Radio value="bank_transfer" style={{ padding: 12, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                        <div>
                          <Text strong>Bank Transfer</Text>
                          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                            One-time transfer to virtual account (non-recurring)
                          </Text>
                        </div>
                      </Radio>
                    </Radio.Group>
                    
                    {paymentMethod === "bank_transfer" && (
                      <Alert
                        message="Bank Transfer Payment"
                        description="You will receive a virtual account number to complete the payment. Your subscription will activate immediately after payment confirmation."
                        type="info"
                        showIcon
                        style={{ marginTop: 12 }}
                      />
                    )}
                  </div>
                )}

                {/* Add-ons Section */}
                {selectedPlanLocal && (
                  <div className="addons-section" style={{ marginTop: 24 }}>
                    <Title level={5}>Add-ons (Optional)</Title>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                      Extend your plan with additional staff accounts, branches, or e-commerce capabilities.
                    </Text>
                    
                    <Row gutter={[16, 16]}>
                      {/* Additional Staff */}
                      <Col xs={24} sm={12}>
                        <Card size="small" className="addon-card">
                          <div style={{ textAlign: 'center', marginBottom: 8 }}>
                            <UserAddOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                          </div>
                          <Title level={5} style={{ textAlign: 'center', marginBottom: 4 }}>Extra Staff</Title>
                          <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 12, marginBottom: 12 }}>
                            {formatCurrency(staffPricingData.price)}/staff/month
                          </Text>
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Button 
                              size="small" 
                              onClick={() => setAdditionalStaff(Math.max(0, additionalStaff - 1))}
                              disabled={additionalStaff <= 0}
                            >
                              -
                            </Button>
                            <InputNumber
                              min={0}
                              max={100}
                              value={additionalStaff}
                              onChange={(val) => setAdditionalStaff(val || 0)}
                              style={{ width: 60, margin: '0 8px' }}
                              controls={false}
                            />
                            <Button 
                              size="small" 
                              onClick={() => setAdditionalStaff(additionalStaff + 1)}
                            >
                              +
                            </Button>
                          </div>
                          {additionalStaff > 0 && (
                            <Text style={{ display: 'block', textAlign: 'center', marginTop: 8, color: '#1890ff' }}>
                              +{formatCurrency(parseFloat(staffPricingData.price) * additionalStaff * duration)}
                            </Text>
                          )}
                        </Card>
                      </Col>
                      
                      {/* Additional Branches */}
                      <Col xs={24} sm={12}>
                        <Card size="small" className="addon-card">
                          <div style={{ textAlign: 'center', marginBottom: 8 }}>
                            <ShopOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                          </div>
                          <Title level={5} style={{ textAlign: 'center', marginBottom: 4 }}>Extra Branches</Title>
                          <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 12, marginBottom: 12 }}>
                            {formatCurrency(branchPricingData.price)}/branch/month
                          </Text>
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Button 
                              size="small" 
                              onClick={() => setAdditionalBranches(Math.max(0, additionalBranches - 1))}
                              disabled={additionalBranches <= 0}
                            >
                              -
                            </Button>
                            <InputNumber
                              min={0}
                              max={100}
                              value={additionalBranches}
                              onChange={(val) => setAdditionalBranches(val || 0)}
                              style={{ width: 60, margin: '0 8px' }}
                              controls={false}
                            />
                            <Button 
                              size="small" 
                              onClick={() => setAdditionalBranches(additionalBranches + 1)}
                            >
                              +
                            </Button>
                          </div>
                          {additionalBranches > 0 && (
                            <Text style={{ display: 'block', textAlign: 'center', marginTop: 8, color: '#52c41a' }}>
                              +{formatCurrency(parseFloat(branchPricingData.price) * additionalBranches * duration)}
                            </Text>
                          )}
                        </Card>
                      </Col>
                      

                    </Row>
                  </div>
                )}

                {/* Promo Code Section - Only for eligible currencies */}
                {selectedPlanLocal && !isPaystackUser && isEligibleForWelcom35 && (
                  <div className="promo-section" style={{ marginTop: 24 }}>
                    <Title level={5}>
                      <GiftOutlined /> Promo Code
                    </Title>
                    <div className="promo-input-group">
                      <Input
                        placeholder="Enter promo code (e.g., WELCOM35)"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        style={{ width: 200 }}
                        disabled={promoCodeStatus?.valid}
                      />
                      {promoCodeStatus?.valid ? (
                        <Button onClick={handleClearPromoCode}>Clear</Button>
                      ) : (
                        <Button
                          type="primary"
                          onClick={handleValidatePromoCode}
                          loading={isValidatingPromo}
                        >
                          Apply
                        </Button>
                      )}
                    </div>
                    {promoCodeStatus?.valid && (
                      <Tag color="success" style={{ marginTop: 8 }}>
                        <CheckCircleOutlined /> {promoCodeStatus.discountPercent}% discount applied!
                      </Tag>
                )}
                {promoCodeStatus && !promoCodeStatus.valid && promoCodeStatus.error && (
                  <Tag color="error" style={{ marginTop: 8 }}>
                    <CloseCircleOutlined /> {promoCodeStatus.error}
                  </Tag>
                )}
                {isEligibleForWelcom35 && !promoCodeStatus?.valid && (
                  <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                    Use code <strong>WELCOM35</strong> for 5% off your first payment!
                  </Text>
                )}
              </div>
            )}

            {/* Price Summary */}
            {selectedPlanLocal && (
              <div className="price-summary" style={{ marginTop: 24 }}>
                <Card className="summary-card">
                  <Title level={5}>Order Summary</Title>
                  {paymentMethod === "bank_transfer" && (
                    <Alert
                      message="Non-Recurring Payment"
                      description="This is a one-time payment. Your subscription will be valid for the selected billing period and will not auto-renew."
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  {(() => {
                    const breakdown = getPricingBreakdown();
                    const billingConfig = getCurrentBillingPeriod();
                    return (
                      <>
                        <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <Text>Plan: {selectedPlanLocal.name}</Text>
                          <Text>{formatCurrency(selectedPlanLocal.price)}/mo</Text>
                        </div>
                        <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <Text>Billing Period: {billingConfig.label}</Text>
                          <Text>{formatCurrency(parseFloat(selectedPlanLocal.price) * billingConfig.months)}</Text>
                        </div>
                        
                        {/* Addon costs */}
                        {additionalStaff > 0 && (
                          <div className="summary-row addon" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Text><UserAddOutlined /> Extra Staff (×{additionalStaff})</Text>
                            <Text>{formatCurrency(parseFloat(staffPricingData.price) * additionalStaff * billingConfig.months)}</Text>
                          </div>
                        )}
                        {additionalBranches > 0 && (
                          <div className="summary-row addon" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Text><ShopOutlined /> Extra Branches (×{additionalBranches})</Text>
                            <Text>{formatCurrency(parseFloat(branchPricingData.price) * additionalBranches * billingConfig.months)}</Text>
                          </div>
                        )}

                        
                        {/* Existing overage charges */}
                        {getExtraStaffCount() > additionalStaff && (
                          <div className="summary-row overage" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Text type="secondary">Existing extra staff (×{getExtraStaffCount() - additionalStaff})</Text>
                            <Text type="secondary">{formatCurrency(parseFloat(staffPricingData.price) * (getExtraStaffCount() - additionalStaff) * billingConfig.months)}</Text>
                          </div>
                        )}
                        {getExtraBranchCount() > additionalBranches && (
                          <div className="summary-row overage" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Text type="secondary">Existing extra branches (×{getExtraBranchCount() - additionalBranches})</Text>
                            <Text type="secondary">{formatCurrency(parseFloat(branchPricingData.price) * (getExtraBranchCount() - additionalBranches) * billingConfig.months)}</Text>
                          </div>
                        )}
                        
                        <Divider style={{ margin: "12px 0" }} />
                        
                        {/* Subtotal */}
                        <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <Text>Subtotal</Text>
                          <Text>{formatCurrency(breakdown.subtotal)}</Text>
                        </div>
                        
                        {/* Annual discount */}
                        {breakdown.annualDiscount > 0 && (
                          <div className="summary-row discount" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Text type="success">Annual Discount (1 Month Free)</Text>
                            <Text type="success">-{formatCurrency(breakdown.annualDiscount)}</Text>
                          </div>
                        )}
                        
                        {/* Promo code discount */}
                        {breakdown.promoDiscount > 0 && (
                          <div className="summary-row discount" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Text type="success">Promo Discount ({promoCodeStatus.discountPercent}%)</Text>
                            <Text type="success">-{formatCurrency(breakdown.promoDiscount)}</Text>
                          </div>
                        )}
                        
                        {/* VAT */}
                        {breakdown.vatAmount > 0 && (
                          <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Text type="secondary">VAT ({getVatRate()}%)</Text>
                            <Text type="secondary">{formatCurrency(breakdown.vatAmount)}</Text>
                          </div>
                        )}
                        
                        <Divider style={{ margin: "12px 0" }} />
                        
                        {/* Proration info for mid-subscription upgrades */}
                        {isMidSubscriptionUpgrade && prorationPreview && (
                          <>
                            <Alert
                              message="Mid-Subscription Upgrade"
                              description={
                                <div style={{ fontSize: 12 }}>
                                  <p style={{ margin: '4px 0' }}>
                                    <strong>Days remaining:</strong> {prorationPreview.days_remaining} days
                                  </p>
                                  <p style={{ margin: '4px 0' }}>
                                    <strong>Current monthly:</strong> {formatCurrency(prorationPreview.current_monthly)}
                                  </p>
                                  <p style={{ margin: '4px 0' }}>
                                    <strong>New monthly:</strong> {formatCurrency(prorationPreview.new_monthly)}
                                  </p>
                                </div>
                              }
                              type="info"
                              showIcon
                              style={{ marginBottom: 12 }}
                            />
                            <div className="summary-row total" style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text strong style={{ fontSize: 18 }}>Prorated Amount</Text>
                              <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                                {formatCurrency(prorationPreview.prorated_amount)}
                              </Text>
                            </div>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                              This is the prorated charge for the remainder of your billing period.
                            </Text>
                          </>
                        )}
                        
                        {/* Loading proration */}
                        {isMidSubscriptionUpgrade && isLoadingProration && (
                          <div style={{ textAlign: 'center', padding: 12 }}>
                            <Spin size="small" /> Calculating prorated amount...
                          </div>
                        )}
                        
                        {/* Regular total (for new subscriptions or non-prorated) */}
                        {!isMidSubscriptionUpgrade && (
                          <div className="summary-row total" style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text strong style={{ fontSize: 18 }}>Total</Text>
                            <Text strong style={{ fontSize: 18 }}>
                              {formatCurrency(breakdown.total)}
                            </Text>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </Card>
              </div>
            )}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <Modal
      title={
        <div className="modal-title">
          <CrownOutlined style={{ marginRight: 8 }} />
          {isTrialMode 
            ? `Start Your ${TRIAL_PERIOD_DAYS}-Day Trial`
            : (isFreePlan ? "Upgrade Your Account" : "Manage Subscription")
          }
        </div>
      }
      open={isOpen}
      onCancel={isTrialMode ? undefined : (isUndismissableForFreePlan ? undefined : onClose)}
      closable={isTrialMode ? false : !isUndismissableForFreePlan}
      maskClosable={isTrialMode ? false : !isUndismissableForFreePlan}
      keyboard={isTrialMode ? false : !isUndismissableForFreePlan}
      width={isTrialMode ? 500 : 800}
      footer={getModalFooter()}
      className="new-subscription-modal"
    >
      {renderContent()}
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  subscription: state.subscription,
  allBranches: state.branch.allBranches,
  loadingAllBranches: state.branch.loadingAllBranches,
});

export default connect(mapStateToProps, {
  fetchAvailablePlans,
  fetchCurrentSubscription,
  validatePromoCode,
  clearPromoCodeStatus,
  createStripeCheckout,
  initializePaystackPayment,
  verifyPaystackPayment,
  setSelectedPlan,
  determinePaymentProvider,
  cancelSubscription,
  resetSubscriptionState,
  previewPaystackUpgrade,
  addPaystackStaff,
  addPaystackBranches,
  upgradeSubscription,
  signoutUser,
  getAllBusinessBranches,
  addMerchantCurrentPlan,
  getPricingPlanData,
})(NewSubscriptionModal);
